// @flow

import _ from 'lodash';

import { getAge } from 'client/utils/member';
import { getFormattedDob } from 'client/utils/format';

import BaseApplicantInput from 'client/models/health/BaseApplicantInput';
import {
  type ConditionsType,
  MEMBER_APPLICATION_TYPES,
  MEMBER_TYPES,
  type UsageLevelType,
} from 'client/constants/health';

import {
  Session_Member,
  Session_Member_Application,
  type SessionMemberType,
} from 'client/models/health/Members';
import { type SessionMemberDrugType } from 'client/models/health/DrugInfoModel';

import type { IfpAppType } from 'web-core/types/ifp-app';
import type { MemberType } from 'web-core/types/member';

type StrictMemberType = {
  ...$Exact<MemberType>,
  illnesses: Array<ConditionsType>,
};

export const getBaseApplicantInputs = (
  members: Array<Object>,
  usageLevel?: UsageLevelType,
  providers: Array<number> = [],
  drugs: Array<SessionMemberDrugType> = [],
): Array<Object> =>
  _.map(
    members,
    ({ age, gender, id, illnesses, tobacco, type }) =>
      new BaseApplicantInput({
        age,
        gender,
        isTobaccoUser: tobacco === true,
        preConditions: illnesses,
        type,
        uuid: id,
        usageLevel,
        providers,
        drugs,
      }),
  );

export const getCoveredAndMedicaidMembers = (members: Array<Object>): Array<Object> =>
  _.filter(members, member =>
    _.includes(
      [MEMBER_APPLICATION_TYPES.COVERAGE, MEMBER_APPLICATION_TYPES.MEDICAID],
      member.member_application.type,
    ));

export const getCoveredMembers = (members: Array<Object>): Array<Object> =>
  _.filter(members, member => MEMBER_APPLICATION_TYPES.COVERAGE === member.member_application.type);

export const getMedicaidMembers = (members: Array<Object>): Array<Object> =>
  _.filter(members, member => MEMBER_APPLICATION_TYPES.MEDICAID === member.member_application.type);

export const getHouseholdMembers = (members: Array<Object>): Array<Object> =>
  _.filter(
    members,
    member => MEMBER_APPLICATION_TYPES.HOUSEHOLD === member.member_application.type,
  );

export const hasAtLeastOneCoveredApplicants = (
  members: Array<Object>,
  formData: Object,
): boolean => {
  const atLeastOneCoveredApplicants = _.some(members, member => formData[member.id].coverage);
  return atLeastOneCoveredApplicants;
};

type CreateMemberForNewAppParams = {
  ifpApp: IfpAppType,
  members: Array<MemberType>,
  sessionMembers: Array<SessionMemberType>,
  save: Function,
  saveMembersToApp: Function,
  user: Object,
};

export const createMemberForNewApp = async ({
  ifpApp,
  members,
  sessionMembers,
  save,
  saveMembersToApp,
  user,
}: CreateMemberForNewAppParams) => {
  if (!user.isLoggedIn) {
    const primaryMember = sessionMembers.find(member => member.type === MEMBER_TYPES.PRIMARY);
    if (!primaryMember) {
      const updatedSessionMembers = [
        new Session_Member({ type: MEMBER_TYPES.PRIMARY, id: user.primaryMemberId }),
      ];
      await save(_.assign({}, user, { members: updatedSessionMembers }));
    }
  } else {
    const primaryMember = members.find(member => member.type === MEMBER_TYPES.PRIMARY);
    if (!primaryMember) {
      const newMember = _.assign(
        new Session_Member({ type: MEMBER_TYPES.PRIMARY, id: user.primaryMemberId }),
        {
          user_id: user.oid,
        },
      );
      await saveMembersToApp([newMember], ifpApp.app_id, user);
    }
  }
};

type SaveSessionMembersParams = {
  formData: Object,
  onSubmit: Function,
  resetApplicants: Function,
  save: Function,
  sessionMembers: Array<SessionMemberType>,
  user: Object,
};

export const saveSessionMembers = async ({
  formData,
  onSubmit,
  resetApplicants,
  save,
  sessionMembers,
  user,
}: SaveSessionMembersParams): Promise<Array<Object>> => {
  const updatedSessionMembers = sessionMembers.map(({ id, illnesses, type }) => {
    const { coverage, dob, gender, tobacco } = formData[id];
    return new Session_Member({
      age: getAge(dob),
      dob: getFormattedDob(dob, 'MM-DD-YYYY'),
      gender,
      id,
      illnesses,
      status:
        coverage === true ? MEMBER_APPLICATION_TYPES.COVERAGE : MEMBER_APPLICATION_TYPES.HOUSEHOLD,
      tobacco,
      type,
    });
  });
  const updatedSessionApplications = sessionMembers.map(({ id }) => {
    const { coverage } = formData[id];
    return new Session_Member_Application({
      member_id: id,
      type:
        coverage === true ? MEMBER_APPLICATION_TYPES.COVERAGE : MEMBER_APPLICATION_TYPES.HOUSEHOLD,
    });
  });
  updatedSessionMembers.forEach((member) => {
    const memberId = _.get(member, 'id');
    const application = updatedSessionApplications.find(m => _.get(m, 'member_id') === memberId);
    _.set(member, 'member_application', application);
  });
  await onSubmit();
  await resetApplicants();
  await save(
    _.assign({}, user, {
      member_applications: updatedSessionApplications,
      members: updatedSessionMembers,
    }),
  );
  return updatedSessionMembers;
};

type SaveMembersParams = {
  formData: Object,
  ifpApp: IfpAppType,
  members: Array<StrictMemberType>,
  onSubmit: Function,
  resetApplicants: Function,
  saveMembersToApp: Function,
  user: Object,
};

export const saveMembers = async ({
  formData,
  ifpApp,
  members,
  onSubmit,
  resetApplicants,
  saveMembersToApp,
  user,
}: SaveMembersParams): Promise<Array<Object>> => {
  const newMembers = _.map(members, ({ id, type, illnesses }) => {
    const { coverage, dob, gender, tobacco } = formData[id];
    return _.assign(
      new Session_Member({
        age: getAge(dob),
        dob: getFormattedDob(dob, 'MM-DD-YYYY'),
        gender,
        id,
        illnesses,
        status:
          coverage === true
            ? MEMBER_APPLICATION_TYPES.COVERAGE
            : MEMBER_APPLICATION_TYPES.HOUSEHOLD,
        tobacco,
        type,
      }),
      { user_id: user.oid },
    );
  });
  await onSubmit();
  await resetApplicants();
  await saveMembersToApp(newMembers, ifpApp.app_id, user);
  return newMembers;
};

type RemoveMemberParams = {
  deleteMember: Function,
  member: MemberType,
  save: Function,
  sessionMembers: Array<SessionMemberType>,
  user: Object,
};

export const removeMember = async ({
  deleteMember,
  member,
  save,
  sessionMembers,
  user,
}: RemoveMemberParams) => {
  if (!user.isLoggedIn) {
    const updatedSessionMembers = _.reject(sessionMembers, member);
    const updatedSessionMemberApplications = _.reject(
      user.member_applications,
      member.member_application,
    );
    await save(
      _.assign({}, user, {
        members: updatedSessionMembers,
        member_applications: updatedSessionMemberApplications,
      }),
    );
  } else {
    await deleteMember(member.id);
  }
};
